package xim.poc.game.configuration.v0.tactics

import xim.math.Vector2f
import xim.poc.*
import xim.poc.audio.AudioManager
import xim.poc.audio.SystemSound
import xim.poc.game.*
import xim.poc.game.GameEngine.displayName
import xim.poc.game.configuration.constants.MobSkillId
import xim.poc.game.configuration.constants.SkillId
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsBehaviorController
import xim.poc.gl.ByteColor
import xim.poc.ui.ShiftJis
import xim.resource.DatId
import xim.resource.UiMenu
import xim.resource.UiMenuResource

sealed interface SkillSelectChoice {
    fun displayName(): String
}

object WaitChoice: SkillSelectChoice {
    override fun displayName() = "Wait"
}

class PositionChoice(val positionSkill: TacticsPositionSkill): SkillSelectChoice {
    override fun displayName() = positionSkill.displayName()
}

class SkillChoice(val skillId: SkillId): SkillSelectChoice {
    override fun displayName() = skillId.displayName().ifBlank { "Attack" }
}

private data class PreviousScrollSettings(val cursorIndex: Int, val lowestViewable: Int, val turn: Int)

class TacticsSkillSelectUi(val parent: TacticsUiContext?, val match: TacticsMatchInstance, val actorState: ActorState) {

    companion object {
        private const val fakeMenuName = "fake    magic   "

        private var previousSettings: Pair<ActorId, PreviousScrollSettings>? = null

        fun drawSkillDescription(state: UiState, skill: SkillId, actorState: ActorState?, unit: TacticsUnit? = actorState?.getUnit(), inheritanceContext: Boolean = false) {
            val menu = state.latestMenu ?: return
            val menuPosition = state.latestPosition ?: return

            val config = TacticsSkills[skill]

            val (minRange, maxRange) = unit?.getSkillRange(skill)
                ?: (config.minRange to config.maxRange)

            val charges = if (inheritanceContext) {
                config.charges?.let { it / 2 }
            } else {
                unit?.getSkillChargeCount(skill) ?: config.charges
            }

            val chargeString = if (charges != null) {
                val currentUseCount = getSkillUseCount(actorState, skill)
                val remainingUseCount = (charges - currentUseCount).coerceAtLeast(0)

                val proportion = remainingUseCount.toFloat()/charges.toFloat()
                val color = if (proportion > 0.5f) { ShiftJis.colorWhite } else if ( proportion > 0f) { ShiftJis.colorInfo } else { ShiftJis.colorInvalid }

                "\nRemaining uses: ${color}${remainingUseCount}/${charges}${ShiftJis.colorClear}"
            } else {
                ""
            }

            val rangeString = if (minRange == maxRange) {
                "Range: $minRange"
            } else {
                "Range: ${minRange}${ShiftJis.longTilde}${maxRange}"
            }

            val baseCost = getActorSkillCost(actorState, skill).baseCost
            val costString = "${baseCost.type.name.uppercase()}: ${baseCost.value}"

            val effectSizeString = if (config.aoeRange > 0) {
                val type = config.aoeType.name
                "\n[AoE] Type: $type    Effect Range: ${config.aoeRange}"
            } else {
                ""
            }

            val elementString = skill.tacticsElement().toCharacter() ?: ""
            val description = config.description.prependIndent("  ")

            val message = "${skill.displayName()} ${elementString}${chargeString}\n${description}\n$costString    $rangeString$effectSizeString"
            menu.frame.size.y = 10f + 16f * message.split("\n").size

            UiElementHelper.drawString(message, menuPosition + Vector2f(6f, 8f))

            if (config.crossJob != null && config.crossJob.points > 0) {
                val ptString = " (Pts: ${config.crossJob.points})"
                UiElementHelper.drawString("All Jobs$ptString", menuPosition + Vector2f(menu.frame.size.x - 6f, 8f), alignment = TextAlignment.Right)
            }
        }


        private fun getSkillUseCount(actorState: ActorState?, skillId: SkillId): Int {
            val controller = (actorState?.behaviorController as? TacticsBehaviorController) ?: return 0
            return controller.getSkillUsedCount(skillId)
        }
    }

    private var fakeMenuCreated = false

    private val skillDescription = UiState(
        focusMenu = "menu    bluehelp",
        additionalDraw = { drawSkillDescription(it) },
        parentRelative = ParentRelative.RightOfParent,
    ) {
        false
    }

    private val selectContext = UiState(
        additionalDraw = { drawAbilities(it) },
        focusMenu = fakeMenuName,
        menuStacks = MenuStacks.LogStack,
        appendType = AppendType.StackAndAppend,
        childStates = { listOf(skillDescription) },
        resetCursorIndexOnPush = false,
        drawParent = true,
        scrollSettings = ScrollSettings(numElementsInPage = 12) { getOptionCount() },
        locksMovement = true,
        enableCursorMovementKeys = true,
    ) {
        previousSettings = actorState.id to PreviousScrollSettings(
            it.local.cursorIndex,
            it.local.scrollSettings?.lowestViewableItemIndex ?: 0,
            actorState.getMostRecentTurn(),
        )

        if (UiStateHelper.isEnterPressed()) {
            val ability = getSelectedAbility() ?: return@UiState true

            val valid = when (ability) {
                is PositionChoice -> canUseSkill(ability)
                is SkillChoice -> canUseSkill(ability)
                WaitChoice -> true
            }

            if (!valid) {
                AudioManager.playSystemSoundEffect(SystemSound.Invalid)
                return@UiState true
            }

            parent?.handleEnter()
            true
        } else if (UiStateHelper.isEscPressed()) {
            previousSettings = null
            UiStateHelper.popState(SystemSound.MenuClose)
            parent?.handleExit()
            true
        } else {
            false
        }
    }

    init {
        val previous = previousSettings
        if (previous != null && previous.first == actorState.id && previous.second.turn == actorState.getMostRecentTurn()) {
            selectContext.cursorIndex = previous.second.cursorIndex
            selectContext.scrollSettings?.lowestViewableItemIndex = previous.second.lowestViewable
        }
    }

    fun push() {
        makeFakeMenu()
        UiStateHelper.pushState(selectContext)
    }

    fun getSelectedAbility(): SkillSelectChoice? {
        return getChoices().getOrNull(selectContext.getCurrentItemIndex())
    }

    private fun isSelectedUnitCurrentTurn(): Boolean {
        return match.getCurrentTurnUnit()?.id == actorState.id
    }

    private fun getOptionCount(): Int {
        return getChoices().size
    }

    private fun getChoices(): List<SkillSelectChoice> {
        val choices: MutableList<SkillSelectChoice> = match.getAbilities(actorState)
            .map { SkillChoice(it) }
            .toMutableList()

        if (isSelectedUnitCurrentTurn()) { choices += listOf(PositionChoice(TacticsPositionSwap), PositionChoice(TacticsPositionPush), PositionChoice(TacticsPositionPull), WaitChoice)  }

        return choices
    }

    private fun drawAbilities(uiState: UiState) {
        val stackPos = uiState.latestPosition ?: return
        val offset = Vector2f(0f, 0f)

        val scrollSettings = uiState.scrollSettings!!
        val items = getChoices()

        for (i in scrollSettings.lowestViewableItemIndex until scrollSettings.lowestViewableItemIndex + scrollSettings.numElementsInPage) {
            if (i >= items.size) { break }

            val skill = items[i]
            val skillName = skill.displayName()

            val textColor = if (skill is SkillChoice) {
                val icon = UiElementHelper.getSkillIcon(skill.skillId)
                if (icon != null) { UiElementHelper.drawUiElement(lookup = icon.first, index = icon.second, position = offset + stackPos + Vector2f(16f, 4f)) }
                if (canUseSkill(skill)) { ByteColor.half } else { ByteColor.grey }
            } else if (skill is PositionChoice) {
                if (canUseSkill(skill)) { ByteColor.half } else { ByteColor.grey }
            } else {
                ByteColor.half
            }

            UiElementHelper.drawString(text = skillName, offset = offset + stackPos + Vector2f(36f, 8f), color = textColor)
            offset.y += 16f
        }
    }

    private fun canUseSkill(skillChoice: SkillChoice): Boolean {
        val skill = skillChoice.skillId
        if (actorState.hasStatusEffect(StatusEffect.Amnesia) && skill is MobSkillId) { return false }

        val charges = actorState.getUnit().getSkillChargeCount(skill)
        if (charges != null && getSkillUseCount(actorState, skill) >= charges) { return false }

        return match.getCurrentTurnUnit()?.id == actorState.id
                && GameEngine.canBeginSkill(actorState.id, skill)
                && match.getTargets(actorState, skill).isNotEmpty()
    }

    private fun canUseSkill(skillChoice: PositionChoice): Boolean {
        val skill = skillChoice.positionSkill

        return match.getCurrentTurnUnit()?.id == actorState.id
                && match.getTargets(actorState, skill).isNotEmpty()
    }

    private fun drawSkillDescription(state: UiState) {
        val choice = getSelectedAbility() ?: return
        when (choice) {
            is SkillChoice -> drawSkillDescription(state, choice.skillId, actorState)
            is PositionChoice -> drawPositionChoice(state, choice.positionSkill)
            WaitChoice -> drawWaitDescription(state)
        }
    }

    private fun drawWaitDescription(state: UiState) {
        val menu = state.latestMenu ?: return
        val menuPosition = state.latestPosition ?: return

        val message = "Wait\n  Pass turn without performing an action.\nTP: 200"
        menu.frame.size.y = 8f + 16f * message.split("\n").size

        UiElementHelper.drawString(message, menuPosition + Vector2f(6f, 8f))
    }

    private fun drawPositionChoice(state: UiState, skill: TacticsPositionSkill) {
        val menu = state.latestMenu ?: return
        val menuPosition = state.latestPosition ?: return

        val message = "${skill.displayName()}\n  ${skill.description()}\nTP: ${skill.tpCost()}"
        menu.frame.size.y = 8f + 16f * message.split("\n").size

        UiElementHelper.drawString(message, menuPosition + Vector2f(6f, 8f))
    }

    private fun makeFakeMenu() {
        if (fakeMenuCreated) { return }

        val baseResource = UiResourceManager.getMenu("menu    magic   ") ?: throw IllegalStateException("No magic menu?")
        val base = baseResource.uiMenu

        val newMenu = UiMenu(
            name = fakeMenuName,
            frame = base.frame.deepCopy().also { it.size.x += 40f },
            elements = base.elements
        )

        UiResourceManager.register(UiMenuResource(DatId.zero, newMenu))
    }

}